import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  SwapHoriz as SwapHorizIcon,
  Search as SearchIcon,
  LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material';
import { tienePermisosModulo } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader } from 'components/financepro';
import { useProductosMaterialContext } from 'context';

/** Texto fijo enviado al API (el backend exige causa en transdet). */
const CAUSA_TRANSFERENCIA_DEFAULT = 'Transferencia entre localidades';

const PROGRAMA_TRANSFERENCIAS_INVENTARIO = 'transferenciasinventario';

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    height: 46,
    minHeight: 46,
    borderRadius: 1.6,
    bgcolor: '#FFFFFF',
    '& fieldset': { borderColor: FP.color.border },
    '&:hover fieldset': { borderColor: FP.color.borderStrong },
    '&.Mui-focused fieldset': { borderColor: FP.color.navy },
  },
  '& .MuiInputLabel-root': { fontSize: '0.86rem' },
};

const hoyISO = () => {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

/**
 * Transferencias de inventario entre localidades.
 * Origen y destino son seleccionables: cualquier sucursal puede ser origen, y los
 * productos disponibles se cargan de la sucursal origen seleccionada.
 * Si el producto no existe en destino, el servidor lo crea con el mismo código y datos del origen.
 */
const TransferenciasInventario = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog, roleDetalles } = controller;

  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeTransferir = tienePermisosModulo(roleDetalles, PROGRAMA_TRANSFERENCIAS_INVENTARIO);

  const [tablaInventario, setTablaInventario] = useState('productosnoserializados');
  const [localidades, setLocalidades] = useState([]);
  const [localidadOrigen, setLocalidadOrigen] = useState('');
  const [localidadDestino, setLocalidadDestino] = useState('');

  const [fecha, setFecha] = useState(hoyISO);
  const [observaciones, setObservaciones] = useState('');
  const [lineas, setLineas] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const [busquedaProducto, setBusquedaProducto] = useState('');
  const [opcionesProducto, setOpcionesProducto] = useState([]);
  const [productoSel, setProductoSel] = useState(null);
  const [cantidadAgregar, setCantidadAgregar] = useState('1');

  const payloadBase = useMemo(
    () => ({
      tabla_inventario: tablaInventario || "productosnoserializados",
    }),
    [selectedCompany, tablaInventario]
  );

  const localidadesOrigen = useMemo(
    () => localidades.filter((l) => l.texto !== localidadDestino),
    [localidades, localidadDestino]
  );

  const localidadesDestino = useMemo(
    () => localidades.filter((l) => l.texto !== localidadOrigen),
    [localidades, localidadOrigen]
  );

  const cargarLocalidades = useCallback(async () => {
    if (!selectedCompany?.db_empresa) return;
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenerlocalidades', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const locs = data.localidades || [];
        setLocalidades(locs);
        setLocalidadOrigen((prev) => {
          if (prev && locs.some((l) => l.texto === prev)) return prev;
          const defId = selectedCompany?.almacenpordefecto;
          const orig = locs.find((l) => String(l.id) === String(defId));
          return orig?.texto || '';
        });
      }
    } catch {
      showError('Error al cargar localidades');
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor, showError]);

  useEffect(() => {
    cargarLocalidades();
  }, [cargarLocalidades]);

  useEffect(() => {
    if (selectedCompany?.anomesfacturacion && !tablaInventario) {
      setTablaInventario("productosnoserializados");
    }
  }, [selectedCompany, tablaInventario]);

  const buscarProductosApi = useCallback(
    async (texto) => {
      const tab = tablaInventario || "productosnoserializados";
      if (!selectedCompany?.db_empresa || !tab || !localidadOrigen) return [];
      try {
        const response = await fetch(servidor + '/api/productosbackend_buscarproductos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            tabla_inventario: tab,
            descripcion: texto,
            local: localidadOrigen,
          }),
        });
        if (!response.ok) return [];
        const data = await response.json();
        return data.productos || [];
      } catch {
        return [];
      }
    },
    [selectedCompany, usuarioLog, claveLog, servidor, tablaInventario, localidadOrigen]
  );

  useEffect(() => {
    const t = setTimeout(async () => {
      const r = await buscarProductosApi(busquedaProducto.trim());
      setOpcionesProducto(r);
    }, 300);
    return () => clearTimeout(t);
  }, [busquedaProducto, buscarProductosApi]);

  const enriquecerProducto = async (codigo) => {
    const tab = tablaInventario || "productosnoserializados";
    const loc = localidadOrigen;
    if (!codigo || !tab || !loc) return null;
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenerproducto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: tab,
          codigo,
          local: loc,
        }),
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.producto || null;
    } catch {
      return null;
    }
  };

  const agregarLinea = async () => {
    if (!puedeTransferir) {
      showError('No tiene permiso para registrar transferencias');
      return;
    }
    if (!productoSel?.codigo) {
      showError('Seleccione un producto');
      return;
    }
    const q = parseFloat(String(cantidadAgregar).replace(',', '.'));
    if (!q || q <= 0) {
      showError('Indique una cantidad válida');
      return;
    }
    const det = await enriquecerProducto(productoSel.codigo);
    const descripcion =
      det?.descripcion || productoSel.texto?.split(' - ').slice(1).join(' - ') || '';
    const medida = det?.medida || '';
    const codigo = String(det?.codigo || productoSel.codigo).trim();

    setLineas((prev) => {
      const idx = prev.findIndex((l) => l.codigo === codigo);
      if (idx >= 0) {
        const copia = [...prev];
        copia[idx] = { ...copia[idx], cantidad: copia[idx].cantidad + q };
        return copia;
      }
      return [...prev, { id: `${Date.now()}-${prev.length}`, codigo, descripcion, medida, cantidad: q }];
    });
    setProductoSel(null);
    setBusquedaProducto('');
    setCantidadAgregar('1');
  };

  const quitarLinea = (id) => {
    setLineas((prev) => prev.filter((l) => l.id !== id));
  };

  const actualizarCantidadLinea = (id, val) => {
    const q = parseFloat(String(val).replace(',', '.'));
    if (Number.isNaN(q) || q <= 0) return;
    setLineas((prev) => prev.map((l) => (l.id === id ? { ...l, cantidad: q } : l)));
  };

  const handleGuardar = async () => {
    if (!puedeTransferir) {
      showError('No tiene permiso para registrar transferencias');
      return;
    }
    const tab = tablaInventario || selectedCompany?.anomesfacturacion;
    if (!selectedCompany?.db_empresa || !tab) {
      showError('Falta configuración de empresa o tabla de inventario');
      return;
    }
    if (!localidadOrigen) {
      showError('Seleccione la sucursal origen');
      return;
    }
    if (!localidadDestino) {
      showError('Seleccione la localidad destino');
      return;
    }
    if (localidadOrigen === localidadDestino) {
      showError('La sucursal origen y destino no pueden ser la misma');
      return;
    }
    if (!lineas.length) {
      showError('Agregue al menos un producto');
      return;
    }

    setGuardando(true);
    try {
      const response = await fetch(servidor + '/api/productosbackend_transferirinventario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          ...payloadBase,
          tabla_inventario: tab,
          fecha,
          causa: CAUSA_TRANSFERENCIA_DEFAULT,
          observaciones: observaciones.trim(),
          localidad_origen: localidadOrigen,
          localidad_destino: localidadDestino,
          usuario: (usuarioLog || '').slice(0, 100) || 'sistema',
          lineas: lineas.map((l) => ({
            codigo: l.codigo,
            descripcion: l.descripcion,
            medida: l.medida,
            cantidad: l.cantidad,
          })),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo registrar la transferencia');
        return;
      }
      showSuccess(data.mensaje || 'Transferencia registrada');
      setObservaciones('');
      setLineas([]);
    } catch (error) {
      showError('Error de conexión: ' + error.message);
    } finally {
      setGuardando(false);
    }
  };

  if (!puedeTransferir) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          bgcolor: FP.color.surface,
        }}
      >
        <PageHeader title="Productos no serializados" subtitle="Transferencias de inventario entre localidades" />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{
              maxWidth: 560,
              width: '100%',
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              boxShadow: FP.shadow.soft,
              textAlign: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «gestionproductos» en roles para usar transferencias de inventario.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        bgcolor: FP.color.surface,
      }}
    >
      <PageHeader title="Productos no serializados" subtitle="Transferencias de inventario entre localidades" />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <SwapHorizIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
                Datos de la transferencia
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 600, mt: 0.5 }}>
                Origen: cualquier sucursal · Destino: otra sucursal · Solo se listan los productos disponibles en el
                origen. Si el código no existe en destino, se crea allí.
              </Typography>
            </Box>
          </Box>

          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Fecha"
                  type="date"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  sx={INPUT_SX}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth sx={INPUT_SX}>
                  <InputLabel id="ti-loc-orig-label">Sucursal origen</InputLabel>
                  <Select
                    labelId="ti-loc-orig-label"
                    label="Sucursal origen"
                    value={localidadOrigen}
                    onChange={(e) => {
                      const nuevo = e.target.value;
                      setLocalidadOrigen(nuevo);
                      setLineas([]);
                      setProductoSel(null);
                      setBusquedaProducto('');
                    }}
                  >
                    <MenuItem value="">
                      <em>Seleccione…</em>
                    </MenuItem>
                    {localidadesOrigen.map((l) => (
                      <MenuItem key={l.texto} value={l.texto}>
                        {l.texto}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth sx={INPUT_SX}>
                  <InputLabel id="ti-loc-dest-label">Sucursal destino</InputLabel>
                  <Select
                    labelId="ti-loc-dest-label"
                    label="Sucursal destino"
                    value={localidadDestino}
                    onChange={(e) => setLocalidadDestino(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Seleccione…</em>
                    </MenuItem>
                    {localidadesDestino.map((l) => (
                      <MenuItem key={l.texto} value={l.texto}>
                        {l.texto}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Observaciones"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  sx={INPUT_SX}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Productos y líneas
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 600 }}>
              Existencia en sucursal origen seleccionada
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 2.5 }}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  disablePortal
                  options={opcionesProducto}
                  getOptionLabel={(o) => o.texto || ''}
                  inputValue={busquedaProducto}
                  onInputChange={(_, v) => setBusquedaProducto(v)}
                  value={productoSel}
                  onChange={(_, v) => setProductoSel(v)}
                  disabled={!localidadOrigen}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={localidadOrigen ? 'Buscar producto en origen' : 'Seleccione primero la sucursal origen'}
                      InputLabelProps={{ shrink: true }}
                      sx={INPUT_SX}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  label="Cantidad"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={cantidadAgregar}
                  onChange={(e) => setCantidadAgregar(e.target.value)}
                  sx={INPUT_SX}
                />
              </Grid>
              <Grid item xs={6} md="auto">
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={agregarLinea}
                  disabled={!localidadOrigen}
                  sx={{
                    height: 46,
                    borderColor: FP.color.border,
                    color: FP.color.textPrimary,
                    bgcolor: '#FFFFFF',
                    borderRadius: 1.6,
                    textTransform: 'none',
                    fontWeight: 700,
                    '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                  }}
                >
                  Agregar
                </Button>
              </Grid>
            </Grid>

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                mb: 2.5,
                border: `1px solid ${FP.color.border}`,
                borderRadius: 2,
                overflow: 'auto',
                /* Mismo ancho útil que el header de la tarjeta (sin el padding de CardContent). */
                width: { xs: 'calc(100% + 32px)', md: 'calc(100% + 48px)' },
                maxWidth: { xs: 'calc(100% + 32px)', md: 'calc(100% + 48px)' },
                mx: { xs: -2, md: -3 },
              }}
            >
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '14%' }} />
                  <col />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 56 }} />
                </colgroup>
                <TableHead
                  sx={{
                    /* Tema: MuiTableHead con display:block rompe el ancho del encabezado. */
                    display: 'table-header-group',
                    p: 0,
                    borderRadius: 0,
                  }}
                >
                  <TableRow sx={{ bgcolor: FP.color.surfaceAlt, width: '100%' }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: FP.color.textSecondary }}>
                        Código
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: FP.color.textSecondary }}>
                        Descripción
                      </Typography>
                    </TableCell>
                    <TableCell width={100}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: FP.color.textSecondary }}>
                        Medida
                      </Typography>
                    </TableCell>
                    <TableCell width={120}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: FP.color.textSecondary }}>
                        Cantidad
                      </Typography>
                    </TableCell>
                    <TableCell width={56} align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3, color: FP.color.textSecondary }}>
                        Agregue los productos a transferir.
                      </TableCell>
                    </TableRow>
                  ) : (
                    lineas.map((l) => (
                      <TableRow key={l.id} hover>
                        <TableCell>{l.codigo}</TableCell>
                        <TableCell>{l.descripcion}</TableCell>
                        <TableCell>{l.medida}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            variant="outlined"
                            fullWidth
                            inputProps={{ min: 0.0001, step: 'any' }}
                            value={l.cantidad}
                            onChange={(e) => actualizarCantidadLinea(l.id, e.target.value)}
                            sx={{
                              ...INPUT_SX,
                              '& .MuiOutlinedInput-root': { height: 42, minHeight: 42 },
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="error" onClick={() => quitarLinea(l.id)} aria-label="Quitar">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={guardando}
                onClick={handleGuardar}
                sx={{
                  bgcolor: FP.color.navy,
                  color: '#FFFFFF',
                  borderRadius: 1.4,
                  px: 2.2,
                  height: 38,
                  boxShadow: 'none',
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
                }}
              >
                Registrar transferencia
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default TransferenciasInventario;
